import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { useEffect, useRef, useState } from "react";

//Components
import { Modal, ModalBody, ModalFooter, Button, Form, Input, Select, ToggleSwitch } from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';

//Actions
import {
    simProviderAccountCreateRequest,
    simProviderAccountEditRequest,
    simProviderAccountGetRequest,
    searchSimProvidersRequest,
    searchEntityRequest,
    clearRedux,
} from 'Redux/actions';

const CreateEditSimProviderAccount = (props) => {
    const { tableRef } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const formRef = useRef();

    //Local States
    const [name, setName] = useState('');
    const [simProvider, setSimProvider] = useState(null);
    const [simType, setSimType] = useState(null);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [api, setApi] = useState(false);
    const [apn, setApn] = useState("");

    //Redux States
    const {
        openModal,
        loading,
        create,
        update,
        simProviderAccount,
        current,
    } = useSelector(state => state.createEditSimProviderAccountRedux);

    const { simProviders } = useSelector(state => state.generalRedux);

    const closeModal = () => {
        setSimProvider(null);
        setSimType(null);
        setUsername(null);
        setPassword(null);
        setApi(false);
        setApn(null);
        setName('');
        dispatch(clearRedux("SIM_PROVIDER_ACCOUNT_CREATE_EDIT"))
    }

    const applyChanges = () => {
        let payload = {
            name,
            simType,
            simProviderId: simProvider?.id,
            username,
            password,
            api,
            apn,
        }

        if (current?.id) {
            payload.id = current?.id
            dispatch(simProviderAccountEditRequest(payload))
        } else dispatch(simProviderAccountCreateRequest(payload))
    }

    useEffect(() => {
        if (create?.id) {
            NotificationManager.success(
                messages['sim_provider_account_create_success_message'],
                messages['sim_provider_account_create_title'],
                3000
            );
            closeModal();
            tableRef && tableRef?.current?.getData();
            dispatch(searchEntityRequest({ key: "simProviderAccounts" }));
        } else if (create?.error) {
            let message = create?.error

            if (message.includes("name exists already")) {
                message = messages['sim_provider_account_same_name_exits']
            }

            NotificationManager.error(
                message,
                messages['sim_provider_account_create_title'],
                3000
            );
            dispatch(clearRedux("SIM_PROVIDER_ACCOUNT_CREATE_EDIT", { create: null }))
        }

        if (update?.id) {
            NotificationManager.success(
                messages['sim_provider_account_edit_success_message'],
                messages['sim_provider_account_edit_title'],
                3000
            );

            closeModal();
            tableRef && tableRef?.current?.getData();
            dispatch(clearRedux("SIM_PROVIDER_ACCOUNT_CREATE_EDIT", { update: null }))
            dispatch(searchEntityRequest({ key: "simProviderAccounts" }));
        } else if (update?.error) {
            let message = update?.error

            if (message.includes("name exists already")) {
                message = messages['sim_provider_account_same_name_exits']
            }

            NotificationManager.error(
                message,
                messages['sim_provider_account_edit_title'],
                3000
            );
            dispatch(clearRedux("SIM_PROVIDER_ACCOUNT_CREATE_EDIT", { update: null }))
        }

        if (simProviderAccount) {
            setName(simProviderAccount?.name);
            setSimProvider(simProviderAccount?.simProvider);
            setSimType(simProviderAccount?.simType);
            setUsername(simProviderAccount?.username);
            setPassword(simProviderAccount?.password);
            setApn(simProviderAccount?.apn);
            setApi(simProviderAccount?.api);
            dispatch(clearRedux("SIM_PROVIDER_ACCOUNT_CREATE_EDIT", { simProviderAccount: null }))
        }

    }, [create, update, simProviderAccount])

    useEffect(() => {
        if (openModal) {
            dispatch(searchSimProvidersRequest())
            if (current?.id) {
                dispatch(simProviderAccountGetRequest(current?.id))
            }
        }
    }, [openModal])

    return (
        <Modal
            open={openModal}
            title={current?.id ? `${messages['sim_provider_account_edit_title']} (${current?.name})` : messages['sim_provider_account_create_title']}
            handleClose={() => closeModal()}
            position="right"
            loading={loading}
            size="small"
        >
            <ModalBody>
                <div style={{ padding: '0 10px', height: 600 }}>
                    <Form
                        ref={formRef}
                        onValidSubmit={() => applyChanges()}
                    >
                        <div style={{ marginTop: 5 }}>
                            <Select
                                id="sim-providers"
                                label={messages['sim_provider_account_sim_provider']}
                                placeholder={messages['sim_provider_account_sim_provider']}
                                options={simProviders}
                                value={simProvider}
                                textField="name"
                                valueField="id"
                                required
                                requiredMessage={messages['required_field']}
                                onChange={(items = []) => setSimProvider(items)}
                            />
                        </div>
                        <div style={{ marginTop: 5 }}>
                            <Input
                                label={messages['sim_provider_account_name']}
                                placeholder={messages['sim_provider_account_name']}
                                value={name}
                                onChange={setName}
                                maxLength={50}
                                required
                                requiredMessage={messages['required_field']}
                                formatMessage={messages['common_error_format']}
                            />
                        </div>
                        <div style={{ marginTop: 5 }}>
                            <Input
                                label={messages['sim_provider_account_sim_type']}
                                placeholder={messages['sim_provider_account_sim_type']}
                                value={simType}
                                onChange={setSimType}
                                required
                                maxLength={25}
                                requiredMessage={messages['required_field']}
                                formatMessage={messages['common_error_format']}
                            />
                        </div>

                        <div style={{ marginTop: 5 }}>
                            <Input
                                label="APN"
                                placeholder="APN"
                                value={apn}
                                onChange={setApn}
                                maxLength={65}
                                requiredMessage={messages['required_field']}
                                formatMessage={messages['common_error_format']}
                            />
                        </div>
                        <div style={{ textAlign: 'left', display: '', padding: '0 10px 0 0', width: "30%", marginTop: 5 }}>
                            <label className="input-normal-label">API</label>
                            <ToggleSwitch
                                id="sim-providers-api"
                                medium
                                blueToggle
                                optionLabels={[messages['create_account_yes'], messages['create_account_not']]}
                                checked={api}
                                onChange={(checked) => {
                                    if (!checked) {
                                        setUsername("")
                                        setPassword("")
                                    }
                                    setApi(checked)
                                }}
                            />
                        </div>
                        {
                            api && (
                                <>
                                    <div style={{ marginTop: 5 }}>
                                        <Input
                                            label={messages['sim_provider_account_username']}
                                            placeholder={messages['sim_provider_account_username']}
                                            value={username}
                                            onChange={setUsername}
                                            maxLength={25}
                                            required={api}
                                            requiredMessage={messages['required_field']}
                                            autoComplete="new-password"
                                            formatMessage={messages['common_error_format']}
                                        />
                                    </div>
                                    <div style={{ marginTop: 5 }}>
                                        <Input
                                            type="password"
                                            label={messages['sim_provider_account_password']}
                                            placeholder={messages['sim_provider_account_password']}
                                            value={password}
                                            onChange={setPassword}
                                            maxLength={25}
                                            required={api}
                                            requiredMessage={messages['required_field']}
                                            autoComplete="new-password"
                                            formatMessage={messages['common_error_format']}
                                        />
                                    </div>
                                </>
                            )
                        }
                    </Form>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    className="btn btn-cancel"
                    onClick={() => closeModal()}
                >
                    {messages['common_cancel']}
                </Button>
                <Button
                    className="btn-dark-blue"
                    onClick={() => {
                        formRef?.current?.submit()
                    }}
                >
                    {messages['save']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default CreateEditSimProviderAccount;